// Home.jsx - Home page

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Tile from '../components/layout/Tile';
import KpiValue from '../components/charts/KpiValue';
import BarEnvelopeChart from '../components/charts/BarEnvelopeChart';
import ShortageTable from '../components/charts/ShortageTable';
import OutstandingPOsTable from '../components/charts/OutstandingPOsTable';
import { useSalesKpi, useSalesProgress } from '../hooks/useSalesData';
import { useInventoryKpi, useInventoryTimeline } from '../hooks/useInventoryData';
import { useShortageKpi, useShortageItems, useExcessKpi } from '../hooks/useShortageData';
import { useOutstandingPOs } from '../hooks/usePOData';
import { useInAppSyncStatus } from '../hooks/useInAppSyncStatus';
import { useReorderLevels } from '../components/reorderLevels/useReorderLevels';
import {
  excessCostState, inventoryCostState, kpiHasValue, stockoutSellPriceState,
} from '../utils/kpiValueCoverage';
import { catalogImporting, forecastRunState, orderHistoryImporting } from '../utils/syncGate';
import { CircularProgress, Typography } from '@mui/material';
import '../styles/pages.css';
import '../styles/grid-page.css';

const Home = () => {
  const { t } = useTranslation();
  const { data: salesKpiData, loading: salesKpiLoading, error: salesKpiError } = useSalesKpi();
  const { data: invKpiData, loading: invKpiLoading, error: invKpiError } = useInventoryKpi();
  const { data: shortageKpiData, loading: shortageKpiLoading, error: shortageKpiError } = useShortageKpi();
  const { data: excessKpiData, loading: excessKpiLoading, error: excessKpiError } = useExcessKpi();
  // The forecast run the order import started: while it calculates (or once
  // it failed) the two forecast tiles say so instead of "not enough sales
  // history". While the first import still brings the catalog in, the two
  // stock-valued tiles say it is not synchronized instead of a sum over the
  // stock so far, and the Sales tile holds back its month to date while the
  // order history is still arriving; the Sales progress and Inventory charts
  // hold back their bars the same way. The sidebar pill reads the same query.
  const syncStatus = useInAppSyncStatus();
  const forecastRun = forecastRunState(syncStatus);
  const importing = catalogImporting(syncStatus);
  const ordersImporting = orderHistoryImporting(syncStatus);
  const salesHasValue = !!salesKpiData?.has_orders && !ordersImporting;
  // A store whose forecasts are all zero has no sales to plan from yet: the
  // Sales progress and Stockout risk tiles say so, and the second one offers
  // the reorder-levels dialog. Undefined until the status read lands.
  const { open: openReorderLevels, status: reorderLevelsStatus } = useReorderLevels();
  const hasForecastDemand = reorderLevelsStatus?.has_forecast_demand;

  // The Stockout risk tile is FINAL: raise any change to it with the user
  // instead of making it.
  // Stockout risk is the SELL-price twin of the two cost-valued tiles beside
  // it: its value is (shortage units × sell price), so an item the merchant
  // has not priced shortens it, and the remedy is Data Health's
  // `missing_sell_price` card — a different card from the cost tiles'.
  const stockoutState = useMemo(() => (
    shortageKpiData
      ? stockoutSellPriceState({
        totalItems: shortageKpiData.total_items,
        catalogImporting: importing,
        hasForecasts: shortageKpiData.has_forecasts,
        forecastRun,
        value: shortageKpiData.total_risk_value,
        unpricedItems: shortageKpiData.unpriced_items,
        discloseGap: shortageKpiData.partial_sell_price,
        changePartial: shortageKpiData.change_partial,
      })
      : null
  ), [shortageKpiData, forecastRun, importing]);

  const stockoutHasValue = kpiHasValue(stockoutState);

  // Excess tile, the same seam as the Inventory tile below: the tile shows
  // four things off ONE state — subtitle, tooltip, whether there is a number
  // to render, and whether the % chip may print — rather than each of them
  // re-deriving its own answer from the raw payload fields. Only the states
  // that say what is missing carry a subtitle.
  const excessState = useMemo(() => (
    excessKpiData
      ? excessCostState({
        totalItems: excessKpiData.total_items,
        catalogImporting: importing,
        hasForecasts: excessKpiData.has_forecasts,
        forecastRun,
        itemsWithCost: excessKpiData.items_with_cost,
        value: excessKpiData.latest_value,
        uncostedItems: excessKpiData.uncosted_excess_items,
        discloseGap: excessKpiData.partial_cost,
        changePartial: excessKpiData.change_partial,
      })
      : null
  ), [excessKpiData, forecastRun, importing]);

  const excessHasValue = kpiHasValue(excessState);

  // Inventory tile, same seam. The tile shows three things off one state —
  // subtitle, tooltip and whether there is a number to render at all — so all
  // three read one state. A store whose uncosted items hold no stock is not
  // partial (`items_with_cost < total_items` alone would say it is).
  const inventoryState = useMemo(() => (
    invKpiData
      ? inventoryCostState({
        totalItems: invKpiData.total_items,
        catalogImporting: importing,
        itemsWithCost: invKpiData.items_with_cost,
        value: invKpiData.latest_value,
        uncostedItems: invKpiData.uncosted_inventory_items,
        discloseGap: invKpiData.partial_cost,
        changePartial: invKpiData.change_partial,
      })
      : null
  ), [invKpiData, importing]);

  const inventoryHasValue = kpiHasValue(inventoryState);

  const excessInfoText = useMemo(() => {
    if (!excessState) return undefined;
    const { state, count } = excessState;
    return {
      notSynchronized: () => undefined,
      noForecast: () => t('forecast.empty.title'),
      forecastCalculating: () => t('home.metrics.forecastCalculating'),
      forecastFailed: () => t('home.metrics.forecastFailed'),
      noCost: () => t('home.metrics.addCostPrices'),
      partial: () => t('home.metrics.addCostPrice', { count }),
      partialHistory: () => t('home.metrics.costGapLastMonth'),
      full: () => t('home.metrics.projectedExcessFull'),
    }[state]();
  }, [excessState, t]);

  // Stockout tile, same seam as the two cost tiles. Each KPI tooltip says one
  // thing, and only when the tile needs it said: what the number is (a
  // complete value), what to do (a missing price), or why the % is approximate
  // (last month's missing price). A state the subtitle already names carries
  // none.
  const stockoutInfoText = useMemo(() => {
    if (!stockoutState) return undefined;
    const { state, count } = stockoutState;
    return {
      notSynchronized: () => undefined,
      noForecast: () => t('forecast.empty.title'),
      forecastCalculating: () => t('home.metrics.forecastCalculating'),
      forecastFailed: () => t('home.metrics.forecastFailed'),
      noSellPrice: () => t('home.metrics.addSellPrices'),
      partial: () => t('home.metrics.addSellPrice', { count }),
      partialHistory: () => t('home.metrics.sellPriceGapLastMonth'),
      full: () => t('home.metrics.stockoutRiskFull'),
    }[state]();
  }, [stockoutState, t]);

  const inventoryInfoText = useMemo(() => {
    if (!inventoryState) return undefined;
    const { state, count } = inventoryState;
    return {
      notSynchronized: () => undefined,
      noCost: () => t('home.metrics.addCostPrices'),
      partial: () => t('home.metrics.addCostPrice', { count }),
      partialHistory: () => t('home.metrics.costGapLastMonth'),
      full: () => t('home.metrics.inventoryFull'),
    }[state]();
  }, [inventoryState, t]);

  return (
    <div className="page-container">
      {/* Metrics Row */}
      <div className="tiles-row tiles-strip">
        {/* Not synchronized says so in the subtitle; a month to date, zero
            included, says what it is in the tooltip. */}
        <Tile width="quarter" title={t("home.metrics.sales")}
          subtitle={salesKpiData && !salesHasValue ? t("home.metrics.salesNotSyncedShort") : undefined}
          infoText={salesHasValue ? t('home.metrics.salesFull') : undefined}>
          {salesKpiLoading ? (
            <div className="metric-display">
              <CircularProgress size={24} />
            </div>
          ) : salesKpiError ? (
            <div className="metric-display">
              <Typography variant="body2" color="error">{salesKpiError}</Typography>
            </div>
          ) : salesKpiData ? (
            <KpiValue
              kpiValue={salesHasValue ? salesKpiData.mtd_value : null}
              change={salesHasValue ? salesKpiData.change : null}
              currency={salesKpiData.currency}
              comparison="samePeriodLastMonth"
            />
          ) : null}
        </Tile>

        <Tile width="quarter" title={t("home.metrics.inventory")} subtitle={
          inventoryState?.state === 'notSynchronized' ? t("home.metrics.notSynchronizedShort")
            : inventoryState?.state === 'noCost' ? t("home.metrics.noCostShort")
            : undefined
        } infoText={inventoryInfoText}>
          {invKpiLoading ? (
            <div className="metric-display">
              <CircularProgress size={24} />
            </div>
          ) : invKpiError ? (
            <div className="metric-display">
              <Typography variant="body2" color="error">{invKpiError}</Typography>
            </div>
          ) : invKpiData ? (
            <KpiValue
              kpiValue={inventoryHasValue ? invKpiData.latest_value : null}
              change={inventoryHasValue ? invKpiData.change : null}
              currency={invKpiData.currency}
              comparison="endOfLastMonth"
              neutralColors
            />
          ) : null}
        </Tile>

        <Tile width="quarter" title={t("home.metrics.stockoutRisk")} subtitle={
          stockoutState?.state === 'notSynchronized' ? t("home.metrics.notSynchronizedShort")
            : stockoutState?.state === 'noSellPrice' ? t("home.metrics.noSellPriceShort")
            : undefined
        } infoText={stockoutInfoText}>
          {shortageKpiLoading ? (
            <div className="metric-display">
              <CircularProgress size={24} />
            </div>
          ) : shortageKpiError ? (
            <div className="metric-display">
              <Typography variant="body2" color="error">{shortageKpiError}</Typography>
            </div>
          ) : shortageKpiData ? (
            <KpiValue
              kpiValue={stockoutHasValue ? shortageKpiData.total_risk_value : null}
              change={stockoutHasValue ? shortageKpiData.change : null}
              currency={shortageKpiData.currency}
              comparison="endOfLastMonth"
              invertColors
            />
          ) : null}
        </Tile>

        <Tile width="quarter" title={t("home.metrics.projectedExcess")} subtitle={
          excessState?.state === 'notSynchronized' ? t("home.metrics.notSynchronizedShort")
            : excessState?.state === 'noCost' ? t("home.metrics.noCostShort")
            : undefined
        } infoText={excessInfoText}>
          {excessKpiLoading ? (
            <div className="metric-display">
              <CircularProgress size={24} />
            </div>
          ) : excessKpiError ? (
            <div className="metric-display">
              <Typography variant="body2" color="error">{excessKpiError}</Typography>
            </div>
          ) : excessKpiData ? (
            <KpiValue
              kpiValue={excessHasValue ? excessKpiData.latest_value : null}
              change={excessHasValue ? excessKpiData.change : null}
              currency={excessKpiData.currency}
              comparison="endOfLastMonth"
              invertColors
            />
          ) : null}
        </Tile>
      </div>

      {/* Sales Progress + Inventory Progress Row */}
      <div className="tiles-row">
        <Tile width="half" title={t("home.sections.salesProgress")}>
          <BarEnvelopeChart
            useData={useSalesProgress}
            i18nPrefix="home.salesProgress"
            chartId="sales_progress"
            controls={{ metric: true, granularity: true }}
            defaultGranularity="week"
            noDataKey={hasForecastDemand === false ? 'home.salesProgress.noSalesYet' : undefined}
            importingReason={ordersImporting ? 'orders_not_synced' : undefined}
          />
        </Tile>

        <Tile width="half" title={t("home.sections.inventoryProgress")}>
          {/* 3 past months, the current month, 3 months ahead: `months` counts the current month. */}
          <BarEnvelopeChart useData={useInventoryTimeline} i18nPrefix="home.inventoryProgress" chartId="inventory_progress" controls={{ metric: true, granularity: true }}
            defaultGranularity="week" historyMonths={3} months={4}
            importingReason={importing ? 'inventory_not_synced' : undefined} />
        </Tile>
      </div>

      {/* Shortage Table + Forecast Row */}
      <div className="tiles-row">
        <Tile width="half" title={t("home.sections.stockoutRisk")} infoText={t("home.sections.stockoutRiskInfo")}>
          <ShortageTable
            useItemsData={useShortageItems}
            catalogImporting={importing}
            forecastRun={forecastRun}
            hasForecastDemand={hasForecastDemand}
            onOpenReorderLevels={openReorderLevels}
          />
        </Tile>

        <Tile width="half" title={t("home.sections.outstandingPOs")}>
          <OutstandingPOsTable useItemsData={useOutstandingPOs} />
        </Tile>
      </div>
    </div>
  );
};

export default Home;
